import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import ProfileForm from "@/components/ProfileForm";
import Spinner from "@/components/Spinner";
import { MissionEyebrow } from "@/components/Mission";
import { getProfile, profileForApi } from "@/lib/profileStore";

const COLOR_TIER_CLS = {
  past: "border-slate-200 bg-slate-50 text-slate-500",
  red: "border-red-200 bg-red-50 text-red-700",
  yellow: "border-amber-200 bg-amber-50 text-amber-700",
  green: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

const TYPE_LABEL = {
  ed: "Early Decision", ea: "Early Action", rd: "Regular Decision",
  fa_priority: "Aid priority filing", fafsa_opens: "FAFSA opens",
  css: "CSS Profile", decision: "Aid award notification",
  deposit: "Enrollment deposit", renewal: "Aid renewal",
  fafsa_goal: "FAFSA submission goal",
};

export default function CalendarPage() {
  const [profile, setProfileLocal] = useState(getProfile());
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterSchool, setFilterSchool] = useState("all");
  const [view, setView] = useState("timeline");

  const generate = async () => {
    setError("");
    const colleges = (profile.college_list || []).filter((c) => c.name && c.name.trim().length >= 2);
    if (colleges.length === 0) return setError("Add at least one school to your list.");
    setBusy(true);
    try {
      const r = await api.post("/aw/calendar", {
        application_cycle: profile.application_cycle,
        college_list: colleges.map((c) => ({ name: c.name.trim(), plan: c.plan || "unknown" })),
      });
      setEvents(r.data?.events || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't generate the calendar. Try again.");
    } finally { setBusy(false); }
  };

  const schools = useMemo(() => {
    const s = new Map();
    events.forEach((e) => { if (!s.has(e.school_id)) s.set(e.school_id, e.school_name); });
    return Array.from(s, ([id, name]) => ({ id, name }));
  }, [events]);

  const filtered = useMemo(() =>
    events.filter((e) =>
      (filterType === "all" || e.type === filterType) &&
      (filterSchool === "all" || e.school_id === filterSchool)
    ), [events, filterType, filterSchool]);

  return (
    <AppShell>
      <div className="py-10" data-testid="calendar-root">
        <MissionEyebrow testId="calendar-mission" />
        <h1 className="font-heading italic text-4xl sm:text-5xl text-[#0f172a] mb-3">Financial Aid Calendar</h1>
        <p className="text-slate-600 mb-8 max-w-2xl">
          Every deadline tied to your college list — color-coded by urgency.
          <span className="italic"> Estimated ~</span> dates are AI-projected; verify on the school site.
        </p>

        <ProfileForm profile={profile} onChange={setProfileLocal} requireFields={["college list", "cycle"]} />

        <div className="flex items-center justify-between gap-3 mb-8 flex-wrap">
          <button onClick={generate} disabled={busy} data-testid="calendar-generate-btn"
            className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] text-white text-sm font-medium px-6 py-3 disabled:opacity-60">
            {busy ? "Generating…" : events.length > 0 ? "Refresh deadlines" : "Generate my calendar →"}
          </button>
          {error && <span className="text-sm text-red-700" data-testid="calendar-error">{error}</span>}
        </div>

        {events.length > 0 && (
          <>
            <div className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-5 mb-6 grid sm:grid-cols-4 gap-3" data-testid="calendar-filters">
              <select value={view} onChange={(e) => setView(e.target.value)} data-testid="calendar-view-select"
                className="rounded-xl border border-[#bfdbfe] px-3 py-2 bg-white text-sm">
                <option value="timeline">Timeline view</option>
                <option value="month">Month view</option>
              </select>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="calendar-type-filter"
                className="rounded-xl border border-[#bfdbfe] px-3 py-2 bg-white text-sm">
                <option value="all">All deadline types</option>
                {Object.entries(TYPE_LABEL).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
              </select>
              <select value={filterSchool} onChange={(e) => setFilterSchool(e.target.value)} data-testid="calendar-school-filter"
                className="rounded-xl border border-[#bfdbfe] px-3 py-2 bg-white text-sm sm:col-span-2">
                <option value="all">All schools</option>
                {schools.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
            </div>

            {view === "timeline" ? (
              <ul className="space-y-3" data-testid="calendar-timeline">
                {filtered.map((e) => (
                  <li key={e.event_id} className={`rounded-2xl border px-5 py-4 ${COLOR_TIER_CLS[e.color_tier] || COLOR_TIER_CLS.green}`}
                    data-testid={`calendar-event-${e.event_id}`}>
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                      <div className="text-sm font-medium">{e.title}</div>
                      <div className="text-xs font-mono whitespace-nowrap">
                        <span className="text-3xl font-heading italic mr-2">{Math.abs(e.days_until)}</span>
                        {e.days_until < 0 ? "days ago" : "days"}
                      </div>
                    </div>
                    <div className="text-xs opacity-75 mt-1">{e.date}{e.estimated && <span className="ml-2 italic">• Estimated ~</span>}</div>
                    <div className="text-xs mt-2 leading-relaxed">{e.description}</div>
                    {e.source && <div className="text-[10px] opacity-60 mt-2">Source: {e.source}{e.last_verified && ` • verified ${e.last_verified}`}</div>}
                  </li>
                ))}
              </ul>
            ) : (
              <MonthView events={filtered} />
            )}
          </>
        )}

        {busy && events.length === 0 && (
          <Spinner variant="block" label="Stitching every deadline across your colleges and cycle." testId="calendar-spinner" />
        )}

        {events.length === 0 && !busy && (
          <div className="text-center py-12 text-slate-500 text-sm">
            Add your college list above and click <span className="font-medium">Generate my calendar</span>.
          </div>
        )}
      </div>
    </AppShell>
  );
}

function MonthView({ events }) {
  const groups = useMemo(() => {
    const m = new Map();
    events.forEach((e) => {
      const key = e.date.slice(0, 7);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(e);
    });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [events]);

  return (
    <div className="space-y-8" data-testid="calendar-month">
      {groups.map(([ym, items]) => {
        const [y, m] = ym.split("-").map(Number);
        const label = new Date(y, m - 1, 1).toLocaleDateString("en", { month: "long", year: "numeric" });
        return (
          <div key={ym} className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-5">
            <h3 className="font-heading italic text-2xl text-[#0f172a] mb-3">{label}</h3>
            <ul className="divide-y divide-[#e5e7eb]">
              {items.map((e) => (
                <li key={e.event_id} className="py-3 flex items-center justify-between gap-3"
                  data-testid={`calendar-month-event-${e.event_id}`}>
                  <div>
                    <div className="text-sm font-medium text-[#0f172a]">{e.title}</div>
                    <div className="text-xs text-slate-500">{e.date}{e.estimated && <span className="ml-2 italic">• Estimated ~</span>}</div>
                  </div>
                  <span className={`text-xs font-mono px-2 py-1 rounded-full border ${COLOR_TIER_CLS[e.color_tier] || COLOR_TIER_CLS.green}`}>
                    {e.days_until < 0 ? `${Math.abs(e.days_until)}d ago` : `${e.days_until}d`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
